import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Ship, Anchor, CheckCircle2 } from "lucide-react";

export default function StepVoyage({ data, reload }) {
  const { shipment, voyage, sales_confirmation } = data;
  const isEditable = shipment.current_stage === "voyage";
  const [form, setForm] = useState({
    vessel_name: voyage?.vessel_name || "",
    voyage_number: voyage?.voyage_number || "",
    container_number: voyage?.container_number || "",
    port_of_loading: voyage?.port_of_loading || "",
    port_of_discharge: voyage?.port_of_discharge || "",
    etd: voyage?.etd || sales_confirmation?.etd || "",
    eta: voyage?.eta || sales_confirmation?.eta || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/voyage`, form);
      toast.success("Voyage saved — next: Delivery");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Step 5</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1">Vessel Booking & Voyage</h2>
      <p className="text-sm text-stone-500 mb-4">Container details and port schedule.</p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Vessel Name">
          <input disabled={!isEditable} value={form.vessel_name} onChange={(e) => setForm({ ...form, vessel_name: e.target.value })} className="input" data-testid="voyage-vessel" />
        </Field>
        <Field label="Voyage Number">
          <input disabled={!isEditable} value={form.voyage_number} onChange={(e) => setForm({ ...form, voyage_number: e.target.value })} className="input" data-testid="voyage-number" />
        </Field>
        <div className="col-span-2">
          <Field label="Container Number">
            <input disabled={!isEditable} value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} className="input" data-testid="voyage-container" />
          </Field>
        </div>
        <Field label="Port of Loading">
          <input disabled={!isEditable} value={form.port_of_loading} onChange={(e) => setForm({ ...form, port_of_loading: e.target.value })} className="input" placeholder="Mombasa" data-testid="voyage-pol" />
        </Field>
        <Field label="Port of Discharge">
          <input disabled={!isEditable} value={form.port_of_discharge} onChange={(e) => setForm({ ...form, port_of_discharge: e.target.value })} className="input" placeholder="Rotterdam" data-testid="voyage-pod" />
        </Field>
        <Field label="ETD">
          <input type="date" disabled={!isEditable} value={form.etd} onChange={(e) => setForm({ ...form, etd: e.target.value })} className="input" data-testid="voyage-etd" />
        </Field>
        <Field label="ETA">
          <input type="date" disabled={!isEditable} value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="input" data-testid="voyage-eta" />
        </Field>
      </div>

      {/* Mini timeline */}
      {(form.port_of_loading || form.port_of_discharge) && (
        <div className="mt-5 rounded-xl bg-[#F4E8E9] p-4 flex items-center gap-3">
          <div className="flex flex-col items-center">
            <Anchor className="w-5 h-5 text-[#6B0F1A]" />
            <div className="text-[11px] font-semibold text-[#6B0F1A] mt-1">{form.port_of_loading || "POL"}</div>
            <div className="text-[10px] text-stone-500">{form.etd || "ETD"}</div>
          </div>
          <div className="flex-1 h-[2px] bg-[#A8323F] relative">
            <Ship className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 text-[#6B0F1A]" />
          </div>
          <div className="flex flex-col items-center">
            <Anchor className="w-5 h-5 text-[#6B0F1A]" />
            <div className="text-[11px] font-semibold text-[#6B0F1A] mt-1">{form.port_of_discharge || "POD"}</div>
            <div className="text-[10px] text-stone-500">{form.eta || "ETA"}</div>
          </div>
        </div>
      )}

      {isEditable && (
        <div className="mt-4">
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="voyage-submit">
            <CheckCircle2 className="w-4 h-4" /> Save & continue
          </button>
        </div>
      )}

      <style>{`
        .input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.6rem .9rem; color:#1F1F1F; outline:none; font-size: 0.875rem; }
        .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);}
        .input:disabled { background:#F2EBE8; color:#666666; }
        .btn-primary { display:inline-flex; align-items:center; gap:.4rem; background:#6B0F1A; color:white; font-weight:600; border-radius:.75rem; padding:.625rem 1rem; font-size:.875rem; }
        .btn-primary:hover:not(:disabled) { background:#56121C; }
        .btn-primary:disabled { opacity:.6; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
